<script setup>
import { onMounted, onUnmounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import EvolutionConnectionApi from 'dashboard/api/inbox/evolutionConnection';
import Modal from 'dashboard/components/Modal.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  inbox: { type: Object, required: true },
});
const emit = defineEmits(['close', 'connected']);

const { t } = useI18n();

const POLL_INTERVAL_MS = 4_000;
const QR_REFRESH_AFTER_MS = 25_000;

const loading = ref(true);
const errorMessage = ref('');
const qrBase64 = ref('');
const pairingCode = ref('');

let pollTimer = null;
let qrFetchedAt = 0;

const fetchQr = async () => {
  try {
    const { data } = await EvolutionConnectionApi.connect(props.inbox.id);
    if (data.connected) {
      handleConnected();
      return;
    }
    qrBase64.value = data.qr_base64 || '';
    pairingCode.value = data.pairing_code || '';
    qrFetchedAt = Date.now();
    errorMessage.value = '';
  } catch (error) {
    errorMessage.value = t('INBOX_MGMT.EVOLUTION.MODAL.CONNECT_ERROR');
  } finally {
    loading.value = false;
  }
};

const checkStatus = async () => {
  try {
    const { data } = await EvolutionConnectionApi.status(props.inbox.id);
    if (data.connected) {
      handleConnected();
      return;
    }
    // QR do WhatsApp expira rapido; renova sozinho enquanto o modal estiver aberto.
    if (Date.now() - qrFetchedAt > QR_REFRESH_AFTER_MS) {
      fetchQr();
    }
  } catch (error) {
    // Falha passageira de rede: mantem o QR atual, tenta de novo no proximo ciclo.
  }
};

const handleConnected = () => {
  useAlert(t('INBOX_MGMT.EVOLUTION.MODAL.CONNECTED'));
  emit('connected');
};

onMounted(() => {
  fetchQr();
  pollTimer = setInterval(checkStatus, POLL_INTERVAL_MS);
});

onUnmounted(() => {
  if (pollTimer) clearInterval(pollTimer);
});
</script>

<template>
  <Modal :show="true" size="small" @close="emit('close')">
    <div class="flex flex-col items-center gap-4 p-6 text-center">
      <h3 class="text-lg font-semibold text-n-slate-12">
        {{ t('INBOX_MGMT.EVOLUTION.MODAL.TITLE', { inboxName: inbox.name }) }}
      </h3>
      <p class="text-sm text-n-slate-11">
        {{ t('INBOX_MGMT.EVOLUTION.MODAL.INSTRUCTIONS') }}
      </p>

      <div
        class="flex items-center justify-center size-56 rounded-lg bg-n-slate-2"
      >
        <span v-if="loading" class="text-sm text-n-slate-11">
          {{ t('INBOX_MGMT.EVOLUTION.MODAL.LOADING') }}
        </span>
        <p
          v-else-if="errorMessage"
          class="text-sm text-n-ruby-11 px-4"
        >
          {{ errorMessage }}
        </p>
        <img
          v-else-if="qrBase64"
          :src="qrBase64"
          :alt="t('INBOX_MGMT.EVOLUTION.MODAL.TITLE', { inboxName: inbox.name })"
          class="size-56 object-contain rounded-lg"
        />
      </div>

      <p v-if="pairingCode" class="text-xs text-n-slate-11">
        {{ t('INBOX_MGMT.EVOLUTION.MODAL.PAIRING_CODE', { code: pairingCode }) }}
      </p>

      <Button
        variant="faded"
        color="slate"
        :label="t('INBOX_MGMT.EVOLUTION.MODAL.CLOSE')"
        @click="emit('close')"
      />
    </div>
  </Modal>
</template>
